/**
 * File Card Component
 * Displays a file in a grid or list view
 */

import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Skeleton,
  Chip,
} from '@mui/material'
import {
  Star,
  StarBorder,
  MoreVert,
  Image,
  VideoFile,
  AudioFile,
  Description,
  InsertDriveFile,
  Person,
  Folder,
} from '@mui/icons-material'
import { formatFileSize, getFileTypeIcon, getCategoryName, getCategoryColor, FileCategory } from '@api/types/files'
import type { UserFileDto } from '@api/types/files'

interface FileCardProps {
  file: UserFileDto
  onClick?: () => void
  onFavoriteToggle?: () => void
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void
  isLoading?: boolean
}

function FileTypeIcon({ mimeType }: { mimeType: string }) {
  const type = getFileTypeIcon(mimeType)
  const iconProps = { sx: { fontSize: 48, color: 'text.secondary' } }

  switch (type) {
    case 'image':
      return <Image {...iconProps} />
    case 'video':
      return <VideoFile {...iconProps} />
    case 'audio':
      return <AudioFile {...iconProps} />
    case 'document':
      return <Description {...iconProps} />
    default:
      return <InsertDriveFile {...iconProps} />
  }
}

export function FileCard({
  file,
  onClick,
  onFavoriteToggle,
  onMenuClick,
  isLoading,
}: FileCardProps) {
  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Skeleton variant="rectangular" height={160} />
        <CardContent sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="40%" />
        </CardContent>
      </Card>
    )
  }

  const isImage = file.mimeType.startsWith('image/')
  const displayName = file.displayName || file.fileName

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick
          ? {
              borderColor: 'primary.main',
              boxShadow: 2,
            }
          : {},
      }}
      onClick={onClick}
    >
      {/* Thumbnail or icon */}
      <Box
        sx={{
          height: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          position: 'relative',
        }}
      >
        {isImage && file.thumbnailUrl ? (
          <CardMedia
            component="img"
            height="160"
            image={file.thumbnailUrl}
            alt={displayName}
            sx={{ objectFit: 'cover' }}
          />
        ) : isImage && file.fileUrl ? (
          <CardMedia
            component="img"
            height="160"
            image={file.fileUrl}
            alt={displayName}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <FileTypeIcon mimeType={file.mimeType} />
        )}

        {/* Category chip with icon */}
        <Chip
          icon={file.category === FileCategory.Personal ? <Person /> : <Folder />}
          label={getCategoryName(file.category)}
          size="small"
          color={getCategoryColor(file.category)}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
          }}
        />

        {/* Favorite button */}
        <Tooltip title={file.isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onFavoriteToggle?.()
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)',
              },
            }}
          >
            {file.isFavorite ? (
              <Star sx={{ color: 'warning.main' }} />
            ) : (
              <StarBorder />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* File info */}
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Typography
          variant="subtitle2"
          noWrap
          title={displayName}
          sx={{ fontWeight: 600 }}
        >
          {displayName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatFileSize(file.fileSize)}
        </Typography>
        {file.usageCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Used {file.usageCount} time{file.usageCount !== 1 ? 's' : ''}
          </Typography>
        )}
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {new Date(file.createdDate).toLocaleDateString()}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onMenuClick?.(e)
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  )
}

export function FileCardSkeleton() {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Skeleton variant="rectangular" height={160} />
      <CardContent sx={{ flexGrow: 1 }}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="40%" />
      </CardContent>
    </Card>
  )
}
